import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { formatINR } from "@markfeed/shared";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface PlanDTO {
  code: string;
  name: string;
  tagline: string;
  priceMonthlyPaise: number;
  priceYearlyPaise: number;
  /** Display-only USD price; checkout always charges INR. */
  priceMonthlyUsdCents: number | null;
  features: string[];
  highlighted: boolean;
}

/** Fallback used for prerendering and if the API is unreachable. The database is the source of truth. */
export const FALLBACK_PLANS: PlanDTO[] = [
  { code: "free", name: "Starter", tagline: "Free forever", priceMonthlyPaise: 0, priceYearlyPaise: 0, priceMonthlyUsdCents: 0, highlighted: false, features: ["1 website", "1 project", "50 feedback items", "1 user", "Unlimited clients"] },
  { code: "agency", name: "Professional", tagline: "For freelancers & agencies", priceMonthlyPaise: 49900, priceYearlyPaise: 0, priceMonthlyUsdCents: 999, highlighted: true, features: ["20 websites", "Unlimited projects", "Unlimited feedback", "10 team members", "Client approvals", "Custom branding", "WhatsApp notifications"] },
  { code: "business", name: "Business", tagline: "For growing teams", priceMonthlyPaise: 99900, priceYearlyPaise: 0, priceMonthlyUsdCents: 2499, highlighted: false, features: ["Unlimited websites", "Unlimited projects", "Unlimited team members", "Advanced permissions", "Priority support", "White-label options"] },
];

/** Live plans and GST rate (0 when an admin has switched GST off); falls back to the defaults for prerendering. */
export function usePlans(): { plans: PlanDTO[]; gstPercent: number } {
  const { data } = useQuery({
    queryKey: ["public-plans"],
    queryFn: async () => {
      const res = await fetch("/api/public/plans");
      if (!res.ok) throw new Error("plans");
      return (await res.json()) as { plans: PlanDTO[]; gstPercent: number };
    },
    staleTime: 5 * 60_000,
    retry: 0,
  });
  return { plans: data?.plans.length ? data.plans : FALLBACK_PLANS, gstPercent: data?.gstPercent ?? 18 };
}

type Currency = "INR" | "USD";

/** India → ₹, everyone else → $. Runs after hydration so the prerendered page (₹) matches the first render. */
function guessCurrency(): Currency {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz === "Asia/Kolkata" || tz === "Asia/Calcutta" ? "INR" : "USD";
  } catch {
    return "INR";
  }
}

const formatUSD = (cents: number) => `$${cents % 100 === 0 ? cents / 100 : (cents / 100).toFixed(2)}`;

export function PricingTable() {
  const { plans, gstPercent } = usePlans();
  const [currency, setCurrency] = useState<Currency>("INR");
  useEffect(() => setCurrency(guessCurrency()), []);
  const usd = currency === "USD";
  return (
    <div>
      <div className="mb-10 flex items-center justify-center gap-3">
        <div className="inline-flex rounded-full border border-line bg-white p-1" role="radiogroup" aria-label="Currency">
          {(["INR", "USD"] as const).map((c) => (
            <button
              key={c}
              role="radio"
              aria-checked={currency === c}
              onClick={() => setCurrency(c)}
              className={cn("rounded-full px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer", currency === c ? "bg-ink text-white" : "text-slate-600 hover:text-ink")}
            >
              {c === "INR" ? "₹ INR" : "$ USD"}
            </button>
          ))}
        </div>
      </div>
      <div className="mx-auto grid max-w-5xl gap-4 md:grid-cols-3">
        {plans.map((p) => {
          const free = p.priceMonthlyPaise === 0;
          const price = usd && p.priceMonthlyUsdCents != null ? formatUSD(p.priceMonthlyUsdCents) : formatINR(p.priceMonthlyPaise);
          return (
            <div
              key={p.code}
              className={cn(
                "relative flex flex-col rounded-2xl border bg-white p-6",
                p.highlighted ? "border-brand-600 shadow-[0_20px_50px_-20px_rgba(37,99,235,.45)] ring-1 ring-brand-600" : "border-line",
              )}
            >
              {p.highlighted && <span className="absolute -top-3 left-6 rounded-full bg-brand-600 px-2.5 py-0.5 text-xs font-semibold text-white">Most popular</span>}
              <h3 className="text-lg font-semibold">{p.name}</h3>
              <p className="text-sm text-slate-500">{p.tagline}</p>
              <p className="mt-5 flex items-baseline gap-1">
                <span className="text-4xl font-semibold tracking-tight">{price}</span>
                <span className="text-sm text-slate-500">/month</span>
              </p>
              <p className="mt-1 h-4 text-xs text-slate-500">{free ? "Free forever" : usd ? "Billed monthly · charged in INR" : gstPercent > 0 ? `Billed monthly + ${gstPercent}% GST` : "Billed monthly"}</p>
              <Button asChild className="mt-6" variant={p.highlighted ? "default" : "outline"}>
                <Link to={`/signup?plan=${p.code}`}>{free ? "Start Free" : `Choose ${p.name}`}</Link>
              </Button>
              <ul className="mt-6 space-y-2.5 text-sm text-slate-700">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2">
                    <Check className="mt-0.5 size-4 shrink-0 text-brand-600" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      <p className="mt-6 text-center text-sm text-slate-500">
        {usd
          ? "Payments are processed in Indian rupees (₹), at your card's exchange rate."
          : "Pay with UPI, cards, net banking or UPI AutoPay. GST invoices with your GSTIN."}
      </p>
    </div>
  );
}
